/**
 * The attribute names `r-divider` reads.
 *
 * There are two, and the list is short on purpose. A divider is a line, and a
 * line has four things about it worth setting: how thick it is, whether it is
 * broken up, what colour it is, and how far across the column it runs. Only
 * the first two are here.
 *
 * Colour and width are missing because they already exist. The line is drawn
 * in `currentColor`, so Text Color on the column sets it the same way it sets
 * the colour of an icon, and the element is the column's own box, so Width on
 * the column shortens it. Offering a second control for either would only
 * raise the question of which one wins — the same reasoning that keeps colour
 * and border off `r-button`.
 *
 * Both remaining attributes are closed sets rather than free text, which is
 * what lets them go through the composer's shared select and lets the render
 * path treat anything unrecognised as unset. A stored value reaches a CSS
 * property here, so an allowlist is the boundary that keeps it from being
 * whatever a request happened to write.
 */
export const DIVIDER_STYLE_ATTRIBUTE = 'divider-style-3-resolutions';
export const DIVIDER_THICKNESS_ATTRIBUTE = 'divider-thickness-3-resolutions';

export const DIVIDER_STYLES = ['solid', 'dashed', 'dotted'] as const;
export type DividerStyle = (typeof DIVIDER_STYLES)[number];
export const DEFAULT_DIVIDER_STYLE: DividerStyle = 'solid';

/**
 * Weights rather than a free measurement. Anything under a pixel disappears on
 * a low-density screen and anything much over eight reads as a band rather
 * than a rule, so the useful range is small enough to offer whole.
 */
export const DIVIDER_THICKNESSES = ['1px', '2px', '3px', '4px', '8px'] as const;
export type DividerThickness = (typeof DIVIDER_THICKNESSES)[number];
export const DEFAULT_DIVIDER_THICKNESS: DividerThickness = '1px';

type Attributes = { [key: string]: any } | undefined;

export const resolveDividerStyle = (componentAttributes: Attributes): DividerStyle => {
  const value = componentAttributes?.[DIVIDER_STYLE_ATTRIBUTE];
  return DIVIDER_STYLES.includes(value) ? value : DEFAULT_DIVIDER_STYLE;
};

export const resolveDividerThickness = (componentAttributes: Attributes): DividerThickness => {
  const value = componentAttributes?.[DIVIDER_THICKNESS_ATTRIBUTE];
  return DIVIDER_THICKNESSES.includes(value) ? value : DEFAULT_DIVIDER_THICKNESS;
};
